import { useCallback } from "react";

import { getExtensionValue } from "@medplum/core";
import { EpisodeOfCare } from "@medplum/fhirtypes";
import { AsyncAutocomplete, AsyncAutocompleteProps, useMedplum } from "@medplum/react";

import { Pill } from "@mantine/core";

import { Extensions } from "@/fhir/enums/extensions";
import { formatEpisodePeriodDate } from "@/utils/episode-of-care";
import { getResourcesFromBundle } from "@/utils/medplum";

/** Normalize Mantine form state (single resource or legacy array) to one selected episode */
function normalizeEpisodeOfCareSelection(
  input: EpisodeOfCare | EpisodeOfCare[] | null | undefined,
): EpisodeOfCare | undefined {
  if (input === undefined || input === null) return undefined;
  return Array.isArray(input) ? input[0] : input;
}

interface EpisodeOfCareSearchInputProps
  extends Omit<
    AsyncAutocompleteProps<EpisodeOfCare>,
    "toOption" | "loadOptions" | "value" | "defaultValue"
  > {
  searchCriteria?: Record<string, string>;
  /**
   * Mantine-controlled field value (`getInputProps`): a single EpisodeOfCare. Must not be passed
   * through to AsyncAutocomplete as `value` (Medplum uses `defaultValue` for the selection).
   */
  value?: EpisodeOfCare | EpisodeOfCare[] | null;
  defaultValue?: EpisodeOfCare | EpisodeOfCare[] | null;
}

export default function EpisodeOfCareSearchInput({
  searchCriteria,
  value,
  defaultValue,
  ...props
}: EpisodeOfCareSearchInputProps) {
  const medplum = useMedplum();

  const autocompleteDefault =
    normalizeEpisodeOfCareSelection(value) ?? normalizeEpisodeOfCareSelection(defaultValue);

  const toOption = useCallback((resource: EpisodeOfCare) => {
    const title = getExtensionValue(resource, Extensions.ArtifactTitle) as string;
    const period = resource.period?.end
      ? `${formatEpisodePeriodDate(resource.period?.start)}-${formatEpisodePeriodDate(resource.period?.end)}`
      : formatEpisodePeriodDate(resource.period?.start);
    return {
      label: `${title} - ${period}`,
      value: resource.id!,
      resource,
    };
  }, []);

  const loadOptions = useCallback(
    async (query: string) => {
      const params = {
        // _count must be used here since the filter by title is done on frontend
        _count: 1000,
        ...(searchCriteria ?? {}),
      };
      const { bundle } = await medplum.searchResources("EpisodeOfCare", params);

      return getResourcesFromBundle("EpisodeOfCare", bundle).filter((resource) =>
        (getExtensionValue(resource, Extensions.ArtifactTitle) as string).includes(query),
      );
    },
    [medplum, searchCriteria],
  );

  return (
    <AsyncAutocomplete<EpisodeOfCare>
      maxValues={1}
      toOption={toOption}
      label="Episode of Care"
      loadOptions={loadOptions}
      defaultValue={autocompleteDefault}
      pillComponent={({ onRemove, item }) => (
        <Pill
          withRemoveButton
          onRemove={onRemove}
          removeButtonProps={{ "aria-label": "Remove Episode of Care" }}
        >
          {item.label}
        </Pill>
      )}
      {...props}
    />
  );
}
